// Shared type definitions used by both client and server
// These interfaces define the data models and network protocol

// Validation result type
export interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// Core game entity interfaces
export interface Vector2 {
  x: number;
  y: number;
}

export interface Player {
  id: string;
  pseudonym: string;
  position: Vector2;
  velocity: Vector2;
  isAlive: boolean;
  isGrounded: boolean;
  lastInputTimestamp: number;
}

export interface Obstacle {
  id: string;
  position: Vector2;
  dimensions: Vector2;
  type: ObstacleType;
  spawnTick: number;
}

export enum ObstacleType {
  SPIKE = 'SPIKE',
  WALL = 'WALL',
  PIT = 'PIT'
}

export interface GameState {
  tick: number;
  worldSpeed: number;
  difficulty: number;
  players: Player[];
  obstacles: Obstacle[];
  isActive: boolean;
  winnerId: string | null;
}

export interface Lobby {
  id: string;
  players: Record<string, Player>;
  gameState: GameState;
  scoreboard: Record<string, number>;
  status: LobbyStatus;
  maxPlayers: number;
  createdAt: number;
}

// Network protocol interfaces
export interface InputEvent {
  playerId: string;
  type: InputType;
  timestamp: number;
  sequence: number;
}

// WebSocket message types for client-server communication
export interface WebSocketMessage {
  type: MessageType;
  payload: any;
  timestamp: number;
}

export enum MessageType {
  // Client to Server
  LOBBY_CREATE = 'LOBBY_CREATE',
  PLAYER_JOIN = 'PLAYER_JOIN',
  PLAYER_INPUT = 'PLAYER_INPUT',
  GAME_START = 'GAME_START',
  PING = 'PING',
  
  // Server to Client
  LOBBY_CREATED = 'LOBBY_CREATED',
  GAME_STATE = 'GAME_STATE',
  PLAYER_JOINED = 'PLAYER_JOINED',
  PLAYER_LEFT = 'PLAYER_LEFT',
  GAME_STARTED = 'GAME_STARTED',
  GAME_ENDED = 'GAME_ENDED',
  PLAYER_ELIMINATED = 'PLAYER_ELIMINATED',
  SCOREBOARD_UPDATE = 'SCOREBOARD_UPDATE',
  ERROR = 'ERROR',
  PONG = 'PONG'
}

// Specific message payload types
export interface LobbyCreateMessage {
  // Empty for now, could include options like max players, game mode, etc.
}

export interface LobbyCreatedMessage {
  lobbyId: string;
}

export interface PlayerJoinMessage {
  pseudonym: string;
}

export interface PlayerInputMessage {
  input: InputEvent;
}

export interface GameStartMessage {
  // Empty for now, could include game options
}

export interface GameStateMessage {
  state: StateSnapshot;
}

export interface PlayerJoinedMessage {
  player: PlayerSnapshot;
  playerCount: number;
}

export interface PlayerLeftMessage {
  playerId: string;
  playerCount: number;
}

export interface GameStartedMessage {
  startTime: number;
  players: PlayerSnapshot[];
}

export interface GameEndedMessage {
  winnerId: string | null;
  finalState: StateSnapshot;
  scoreboard: Record<string, number>;
}

export interface PlayerEliminatedMessage {
  playerId: string;
  reason: string;
}

export interface ErrorMessage {
  code: string;
  message: string;
}

export interface ScoreboardUpdateMessage {
  scoreboard: Record<string, { pseudonym: string; score: number }>;
}

export interface StateSnapshot {
  tick: number;
  timestamp: number;
  players: PlayerSnapshot[];
  obstacles: ObstacleSnapshot[];
  worldSpeed: number;
  eliminations: string[];
}

export interface PlayerSnapshot {
  id: string;
  pseudonym: string;
  position: Vector2;
  velocity: Vector2;
  isAlive: boolean;
  isGrounded: boolean;
}

export interface ObstacleSnapshot {
  id: string;
  position: Vector2;
  dimensions: Vector2;
  type: ObstacleType;
}

// Collision detection types
export interface CollisionResult {
  playerId: string;
  obstacleId?: string;
  otherPlayerId?: string;
  collisionType: CollisionType;
  timestamp: number;
}

export enum CollisionType {
  PLAYER_OBSTACLE = 'PLAYER_OBSTACLE',
  PLAYER_PLAYER = 'PLAYER_PLAYER'
}

// Bounding box for collision detection
export interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Input types for better type safety
export enum InputType {
  JUMP = 'JUMP',
  MOVE_LEFT = 'MOVE_LEFT',
  MOVE_RIGHT = 'MOVE_RIGHT'
}

// Game configuration constants
export interface GameConfig {
  readonly TICK_RATE: number;
  readonly MAX_PLAYERS: number;
  readonly WORLD_WIDTH: number;
  readonly WORLD_HEIGHT: number;
  readonly PLAYER_WIDTH: number;
  readonly PLAYER_HEIGHT: number;
  readonly GRAVITY: number;
  readonly JUMP_VELOCITY: number;
}

// Lobby status for better state management
export enum LobbyStatus {
  WAITING = 'WAITING',
  STARTING = 'STARTING',
  IN_PROGRESS = 'IN_PROGRESS',
  FINISHED = 'FINISHED'
}